import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Breadcrumbs from "../../components/Common/Breadcrumb.js";
import { createSubAdminAPI, getAllUsersAPI, updateUserProfileAPI } from "../../helpers/api.ts";

const SubAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const logUser = JSON.parse(localStorage.getItem("user"));

  // Form validation schema for create
  const validationSchema = Yup.object({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be less than 50 characters")
      .required("Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    mobile: Yup.string()
      .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
      .required("Mobile number is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  // Form validation schema for update
  const updateValidationSchema = Yup.object({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be less than 50 characters")
      .required("Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    mobile: Yup.string()
      .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
      .required("Mobile number is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .nullable(),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .nullable(),
    isActive: Yup.boolean(),
  });

  // Formik configuration for create
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobile: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);

      try {
        const payload = {
          name: values.name,
          email: values.email,
          mobile: values.mobile,
          password: values.password,
          role: "SUBADMIN",
          created_by: logUser.id,
        };

        const response = await createSubAdminAPI(payload);

        if (response.success) {
          toast.success("Sub-admin user created successfully!");
          resetForm();
          setModal(false);
          fetchUsers();
        } else {
          toast.error(response.message || "Failed to create sub-admin user");
        }
      } catch (error) {
        console.error("Error creating sub-admin:", error);
        toast.error("An error occurred while creating the sub-admin user");
      } finally {
        setLoading(false);
      }
    },
  });

  // Formik configuration for update
  const updateFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: selectedUser?.name || "",
      email: selectedUser?.email || "",
      mobile: selectedUser?.mobile || "",
      password: "",
      confirmPassword: "",
      isActive: selectedUser?.isActive || false,
    },
    validationSchema: updateValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      setLoading(true);

      try {
        const payload = {
          userId: selectedUser.id,
          name: values.name,
          email: values.email,
          mobile: values.mobile,
          role: "SUBADMIN",
          isActive: values.isActive,
        };

        // Only include password if provided
        if (values.password && values.password.trim() !== "") {
          payload.password = values.password;
        }

        const response = await updateUserProfileAPI(payload);

        if (response.success) {
          toast.success("Sub-admin user updated successfully!");
          resetForm();
          setUpdateModal(false);
          setSelectedUser(null);
          fetchUsers();
        } else {
          toast.error(response.message || "Failed to update sub-admin user");
        }
      } catch (error) {
        console.error("Error updating sub-admin:", error);
        toast.error("An error occurred while updating the sub-admin user");
      } finally {
        setLoading(false);
      }
    },
  });

  // Fetch users function
  const fetchUsers = async () => {
    try {
      const payload = {
        page: currentPage,
        limit: pageSize,
      };

      const response = await getAllUsersAPI(payload);
      
      if (response.success) {
        setUsers(response.users);
        setTotalPages(response.pages);
        setTotal(response.total);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("An error occurred while fetching users");
    }
  };

  // Modal toggle function
  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      formik.resetForm();
    }
  };

  // Update modal toggle function
  const toggleUpdateModal = (user = null) => {
    setSelectedUser(user);
    setUpdateModal(!updateModal);
    if (updateModal) {
      updateFormik.resetForm();
      setSelectedUser(null);
    }
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle page size change
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // Filter sub-admin users
  const subAdminUsers = users.filter(user => user.role === "SUBADMIN");

  // Get status badge color
  const getStatusBadgeColor = (isActive) => {
    return isActive ? "success" : "danger";
  };

  // Get status text
  const getStatusText = (isActive) => {
    return isActive ? "Active" : "Inactive";
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <div className="page-title-box">
              <div className="page-title-right">
                <Breadcrumbs title="Portal" breadcrumbItem="Sub-Admin Management" />
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center flex-column flex-md-row gap-2 mb-4">
                  <h4 className="card-title mb-0">Sub-Admin Users</h4>
                  <div className="d-flex gap-2 w-100 w-md-auto">
                    <Button
                      color="secondary"
                      onClick={() => navigate("/dashboard")}
                      className="btn-sm w-100 w-md-auto"
                    >
                      Back
                    </Button>
                    <Button
                      color="primary"
                      onClick={toggleModal}
                      className="btn-sm w-100 w-md-auto"
                    >
                      <i className="mdi mdi-plus me-1"></i>
                      Create Sub-Admin
                    </Button>
                  </div>
                </div>

                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subAdminUsers.length > 0 ? (
                      subAdminUsers.map((user, index) => (
                        <tr key={user.id}>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>{user.mobile}</td>
                          <td>
                            <Badge color="info" className="badge-soft">
                              {user.role}
                            </Badge>
                          </td>
                          <td>
                            <Badge 
                              color={getStatusBadgeColor(user.isActive)} 
                              className="badge-soft"
                            >
                              {getStatusText(user.isActive)}
                            </Badge>
                          </td>
                          <td>
                            {new Date(user.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <Button
                              color="warning"
                              size="sm"
                              className="me-1"
                              onClick={() => toggleUpdateModal(user)}
                            >
                              <i className="mdi mdi-pencil"></i>
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                            >
                              <i className="mdi mdi-delete"></i>
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center">
                          No sub-admin users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                {/* Pagination */}
                {subAdminUsers.length > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2">Show:</span>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "auto" }}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="ms-2">
                        Showing {subAdminUsers.length} of {total} entries
                      </span>
                    </div>
                    
                    <Pagination className="mb-0">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink 
                          previous 
                          onClick={() => handlePageChange(currentPage - 1)}
                        />
                      </PaginationItem>
                      {[...Array(totalPages)].map((_, i) => (
                        <PaginationItem key={i + 1} active={i + 1 === currentPage}>
                          <PaginationLink onClick={() => handlePageChange(i + 1)}>
                            {i + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))}
                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink 
                          next 
                          onClick={() => handlePageChange(currentPage + 1)}
                        />
                      </PaginationItem>
                    </Pagination>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Create Sub-Admin Modal */}
        <Modal isOpen={modal} toggle={toggleModal} size="lg" className="modal-fullscreen-md-down">
          <ModalHeader toggle={toggleModal}>
            Create Sub-Admin User
          </ModalHeader>
          <Form onSubmit={formik.handleSubmit}>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Enter full name"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.name && formik.errors.name}
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.name}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter email address"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.email && formik.errors.email}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.email}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="mobile">Mobile Number</Label>
                    <Input
                      type="text"
                      id="mobile"
                      name="mobile"
                      placeholder="Enter mobile number"
                      value={formik.values.mobile}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.mobile && formik.errors.mobile}
                    />
                    {formik.touched.mobile && formik.errors.mobile && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.mobile}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      type="password"
                      id="password"
                      name="password"
                      placeholder="Enter password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.password && formik.errors.password}
                    />
                    {formik.touched.password && formik.errors.password && (
                      <div className="invalid-feedback d-block">
                        {formik.errors.password}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={12}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirm password"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={
                        formik.touched.confirmPassword &&
                        formik.errors.confirmPassword
                      }
                    />
                    {formik.touched.confirmPassword &&
                      formik.errors.confirmPassword && (
                        <div className="invalid-feedback d-block">
                          {formik.errors.confirmPassword}
                        </div>
                      )}
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                color="secondary"
                onClick={toggleModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={loading}
              >
                {loading ? "Creating..." : "Create Sub-Admin"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>

        {/* Update Sub-Admin Modal */}
        <Modal isOpen={updateModal} toggle={() => toggleUpdateModal()} size="lg" className="modal-fullscreen-md-down">
          <ModalHeader toggle={() => toggleUpdateModal()}>
            Update Sub-Admin User
          </ModalHeader>
          <Form onSubmit={updateFormik.handleSubmit}>
            <ModalBody>
              <Row>
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="update_name">Full Name</Label>
                    <Input
                      type="text"
                      id="update_name"
                      name="name"
                      placeholder="Enter full name"
                      value={updateFormik.values.name}
                      onChange={updateFormik.handleChange}
                      onBlur={updateFormik.handleBlur}
                      invalid={updateFormik.touched.name && updateFormik.errors.name}
                    />
                    {updateFormik.touched.name && updateFormik.errors.name && (
                      <div className="invalid-feedback d-block">
                        {updateFormik.errors.name}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="update_email">Email Address</Label>
                    <Input
                      type="email"
                      id="update_email"
                      name="email"
                      placeholder="Enter email address"
                      value={updateFormik.values.email}
                      onChange={updateFormik.handleChange}
                      onBlur={updateFormik.handleBlur}
                      invalid={updateFormik.touched.email && updateFormik.errors.email}
                    />
                    {updateFormik.touched.email && updateFormik.errors.email && (
                      <div className="invalid-feedback d-block">
                        {updateFormik.errors.email}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="update_mobile">Mobile Number</Label>
                    <Input
                      type="text"
                      id="update_mobile"
                      name="mobile"
                      placeholder="Enter mobile number"
                      value={updateFormik.values.mobile}
                      onChange={updateFormik.handleChange}
                      onBlur={updateFormik.handleBlur}
                      invalid={updateFormik.touched.mobile && updateFormik.errors.mobile}
                    />
                    {updateFormik.touched.mobile && updateFormik.errors.mobile && (
                      <div className="invalid-feedback d-block">
                        {updateFormik.errors.mobile}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="update_isActive">Status</Label>
                    <Input
                      type="select"
                      id="update_isActive"
                      name="isActive"
                      value={updateFormik.values.isActive}
                      onChange={updateFormik.handleChange}
                      onBlur={updateFormik.handleBlur}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </Input>
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="update_password">New Password (Optional)</Label>
                    <Input
                      type="password"
                      id="update_password"
                      name="password"
                      placeholder="Enter new password"
                      value={updateFormik.values.password}
                      onChange={updateFormik.handleChange}
                      onBlur={updateFormik.handleBlur}
                      invalid={updateFormik.touched.password && updateFormik.errors.password}
                    />
                    {updateFormik.touched.password && updateFormik.errors.password && (
                      <div className="invalid-feedback d-block">
                        {updateFormik.errors.password}
                      </div>
                    )}
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label htmlFor="update_confirmPassword">Confirm New Password</Label>
                    <Input
                      type="password"
                      id="update_confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirm new password"
                      value={updateFormik.values.confirmPassword}
                      onChange={updateFormik.handleChange}
                      onBlur={updateFormik.handleBlur}
                      invalid={
                        updateFormik.touched.confirmPassword &&
                        updateFormik.errors.confirmPassword
                      }
                    />
                    {updateFormik.touched.confirmPassword &&
                      updateFormik.errors.confirmPassword && (
                        <div className="invalid-feedback d-block">
                          {updateFormik.errors.confirmPassword}
                        </div>
                      )}
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                color="secondary"
                onClick={() => toggleUpdateModal()}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={loading}
              >
                {loading ? "Updating..." : "Update Sub-Admin"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </Container>
      <ToastContainer />
    </div>
  );
};

export default SubAdmin;
